import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, isDemoMode } from "../firebase";
import { initialsFor } from "../lib/initials";

const logoUrl = process.env.PUBLIC_URL + "/new_logo.png";

/**
 * The one app chrome. `variant="minimal"` renders the brand alone (Profile,
 * Onboarding); the default renders section links plus the user menu.
 */
/**
 * `inline` renders the bar in normal flow instead of fixed to the viewport —
 * needed by full-height pages (Search) that lay themselves out as a column.
 */
export default function Nav({ profile, active = null, variant = "full", inline = false, onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    if (onLogout) return onLogout();
    if (!isDemoMode) await signOut(auth);
  };

  const navClass = `nav${inline ? " is-inline" : ""}`;

  const brand = (
    <Link to="/dashboard" className="nav-brand">
      <img src={logoUrl} alt="Aline" className="logo" />
    </Link>
  );

  if (variant === "minimal") {
    return <nav className={navClass}>{brand}</nav>;
  }

  return (
    <nav className={navClass}>
      {brand}
      <div className="nav-links">
        <Link to="/dashboard" className={`nav-link${active === "connections" ? " active" : ""}`}>
          Connections
        </Link>
        <Link to="/search" className={`nav-link${active === "search" ? " active" : ""}`}>
          Search
        </Link>
      </div>
      <div className="nav-right" ref={menuRef}>
        <button
          className="nav-user-btn"
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <div className="nav-avatar">
            {profile?.profile_photo_url ? (
              <img src={profile.profile_photo_url} alt={profile.full_name || "Profile"} />
            ) : (
              initialsFor(profile?.full_name)
            )}
          </div>
          <span className="nav-user-name">{profile?.full_name}</span>
          <span className="nav-chevron" aria-hidden="true">▾</span>
        </button>
        {menuOpen && (
          <div className="nav-dropdown" role="menu">
            <Link to="/profile" role="menuitem" onClick={() => setMenuOpen(false)}>
              Profile &amp; Privacy
            </Link>
            <button className="nav-dropdown-danger" role="menuitem" onClick={handleLogout}>
              Log out
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
